import React, { useState } from 'react';
import { Layout, Menu, Breadcrumb, Avatar, Dropdown, Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const { Header, Sider, Content } = Layout;

interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  children?: MenuItem[];
  path: string;
}

type MainLayoutChildren = React.ReactNode | ((collapsed: boolean) => React.ReactNode);

interface MainLayoutProps {
  children: MainLayoutChildren;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [defaultOpenKeys] = useState(['annotation']);

  // 菜单项配置
  const menuItems: MenuItem[] = [
    {
      key: 'annotation',
      icon: <TagOutlined />,
      label: '标注',
      path: '',
      children: [
        {
          key: 'card-annotation',
          label: '卡片数据标注',
          path: '/card-annotation',
        },
      ],
    },
    {
      key: 'data-space',
      icon: <DatabaseOutlined />,
      label: '数据空间',
      path: '',
      children: [
        {
          key: 'dataset-management',
          label: '标注数据集',
          path: '/dataset-management',
        },
      ],
    },
    {
      key: 'error-pages',
      icon: <AppstoreOutlined />,
      label: '错误页',
      path: '',
      children: [
        {
          key: 'data-load-error',
          label: '加载失败',
          path: '/data-load-error',
        },
        {
          key: 'card-not-found',
          label: '找不到数据',
          path: '/card-not-found',
        },
      ],
    },
  ];

  // 处理菜单点击
  const handleMenuClick = (key: string) => {
    const findPath = (items: MenuItem[]): string | undefined => {
      for (const item of items) {
        if (item.key === key) {
          return item.path;
        }
        if (item.children) {
          const path = findPath(item.children);
          if (path) {
            return path;
          }
        }
      }
      return undefined;
    };

    const path = findPath(menuItems);
    if (path) {
      navigate(path);
    }
  };

  // 生成面包屑
  const generateBreadcrumb = () => {
    const breadcrumbItems = [];
    
    // 根据当前路径确定面包屑结构
    if (location.pathname === '/card-annotation') {
      // 卡片数据标注页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="annotation">
          标注
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="card-annotation">
          卡片数据标注
        </Breadcrumb.Item>
      );
    } else if (location.pathname.includes('/annotation-detail/')) {
      // 卡片详情页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="annotation">
          标注
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="card-annotation">
          <Link to="/card-annotation">卡片数据标注</Link>
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="annotation-detail">
          卡片详情
        </Breadcrumb.Item>
      );
    } else if (location.pathname === '/dataset-management') {
      // 标注数据集页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="data-space">
          数据空间
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="dataset-management">
          标注数据集
        </Breadcrumb.Item>
      );
    } else if (location.pathname === '/dataset-management/create') {
      // 创建数据集页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="data-space">
          数据空间
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="dataset-management">
          <Link to="/dataset-management">标注数据集</Link>
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="create-dataset">
          创建数据集
        </Breadcrumb.Item>
      );
    } else if (location.pathname.includes('/dataset-management/add-data/')) {
      // 添加数据页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="data-space">
          数据空间
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="dataset-management">
          <Link to="/dataset-management">标注数据集</Link>
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="add-data">
          添加数据
        </Breadcrumb.Item>
      );
    } else if (location.pathname.includes('/dataset-management/detail/')) {
      // 数据集详情页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="data-space">
          数据空间
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="dataset-management">
          <Link to="/dataset-management">标注数据集</Link>
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="dataset-detail">
          数据集详情
        </Breadcrumb.Item>
      );
    } else if (location.pathname === '/card-not-found') {
      // 卡片不存在页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="annotation">
          标注
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="card-annotation">
          <Link to="/card-annotation">卡片数据标注</Link>
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="card-not-found">
          卡片不存在
        </Breadcrumb.Item>
      );
    } else if (location.pathname === '/data-load-error') {
      // 数据加载失败页面
      breadcrumbItems.push(
        <Breadcrumb.Item key="error">
          错误页面
        </Breadcrumb.Item>
      );
      breadcrumbItems.push(
        <Breadcrumb.Item key="data-load-error">
          数据加载失败
        </Breadcrumb.Item>
      );
    } else {
      // 默认情况，根据路径推断
      if (location.pathname.includes('card-annotation')) {
        breadcrumbItems.push(
          <Breadcrumb.Item key="annotation">
            标注
          </Breadcrumb.Item>
        );
        breadcrumbItems.push(
          <Breadcrumb.Item key="card-annotation">
            卡片数据标注
          </Breadcrumb.Item>
        );
      } else if (location.pathname.includes('dataset-management')) {
        breadcrumbItems.push(
          <Breadcrumb.Item key="data-space">
            数据空间
          </Breadcrumb.Item>
        );
        breadcrumbItems.push(
          <Breadcrumb.Item key="dataset-management">
            标注数据集
          </Breadcrumb.Item>
        );
      }
    }
    
    return breadcrumbItems;
  };

  // 用户下拉菜单
  const userMenu = (
    <Menu
      items={[
        {
          key: '1',
          label: '个人中心',
        },
        {
          key: '2',
          label: '设置',
        },
        {
          key: '3',
          label: '退出登录',
        },
      ]}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh', overflow: 'hidden' }}>
      <Header style={{ 
        padding: '0 24px', 
        background: '#fff', 
        display: 'flex', 
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: 64
      }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
          数据标注平台
        </div>
      </Header>
      <Layout style={{ marginTop: 64, height: 'calc(100vh - 64px)' }}>
        <Sider 
          width={220} 
          collapsible 
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ 
            background: '#001529',
            position: 'fixed',
            left: 0,
            top: 64,
            bottom: 0,
            zIndex: 999
          }}
          theme="dark"
        >
          {/* 悬浮折叠按钮 */}
          <div 
            style={{
              position: 'absolute',
              right: -16,
              top: 72,
              zIndex: 1,
              width: 16,
              height: 48,
              background: '#001529',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '0 4px 4px 0'
            }}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <MenuUnfoldOutlined style={{ color: 'rgba(255,255,255,0.65)' }} />
            ) : (
              <MenuFoldOutlined style={{ color: 'rgba(255,255,255,0.65)' }} />
            )}
          </div>
          
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname.split('/')[1] || 'card-annotation']}
            defaultOpenKeys={defaultOpenKeys}
            style={{ 
              background: '#001529',
              borderRight: '1px solid #030303',
              marginTop: '16px',
              height: 'calc(100% - 16px)',
              overflowY: 'auto'
            }}
            items={menuItems.map(item => {
              if (item.children) {
                return {
                  key: item.key,
                  icon: item.icon,
                  label: item.label,
                  children: item.children.map(child => ({
                    key: child.key,
                    label: child.label,
                    onClick: () => handleMenuClick(child.key),
                    style: {
                      background: location.pathname.includes(child.path) ? '#1890ff' : '#000c17',
                      borderLeft: location.pathname.includes(child.path) ? '3px solid #1890ff' : '3px solid transparent',
                      margin: 0,
                      paddingLeft: collapsed ? '24px' : '48px',
                      color: location.pathname.includes(child.path) ? '#fff' : 'rgba(255, 255, 255, 0.65)'
                    }
                  })),
                };
              }
              return {
                key: item.key,
                icon: item.icon,
                label: item.label,
                onClick: () => handleMenuClick(item.key),
              };
            })}
          />
        </Sider>
        <Layout style={{ 
          padding: '0 24px 24px', 
          marginLeft: collapsed ? 80 : 220,
          height: '100%'
        }}>
          <Breadcrumb style={{ margin: '16px 0' }}>
            {generateBreadcrumb()}
          </Breadcrumb>
          <Content
            style={{
              background: '#fff',
              padding: 24,
              margin: 0,
              minHeight: 280,
              borderRadius: '4px',
              height: 'calc(100% - 64px)',
              overflowY: 'auto'
            }}
          >
            {typeof children === 'function' ? (children as (collapsed: boolean) => React.ReactNode)(collapsed) : children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;